import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  BillingCharge,
  BillingChargeKind,
  PaymentMethod,
  Tenant,
} from '@kawai/shared';
import {
  billingChargeKindLabel,
  bankTransferDisplayStatusLabel,
  computeTaxFromNet,
  defaultBankTransferDueDate,
  formatYen,
  isOfflinePaymentMethod,
  paymentMethodLabel,
  resolveBankTransferDisplayStatus,
  todayJst,
} from '@kawai/shared';
import { Button, Chip, Input, Select, ZebraTable } from '@kawai/ui';
import { useTenants } from '../lib/hooks/useTenants.js';
import {
  useBankTransferCharges,
  useBillingSetPaymentMethod,
  useCreateBankTransferCharge,
  useUpdateBankTransferCharge,
} from '../lib/hooks/useBankTransferBilling.js';
import { bankTransferBillingErrorMessage } from '../lib/api/billingBankTransfer.js';
import { bankTransferStatusChipTone } from '../lib/billingStyles.js';
import { useAuth } from '../lib/auth.js';
import {
  CARD,
  CARD_TITLE,
  MONO,
  ROW_BTN_BRAND,
  ROW_BTN_DANGER,
  ROW_BTN_GHOST,
} from '../lib/uiClasses.js';
import { PageHeader } from '../components/PageHeader.js';
import { Notice } from '../components/Notice.js';

/** 請求作成フォームの金額プリフィル（税抜本体 / 契約 別紙1: 初期 400,000・月額 100,000）。 */
const NET_YEN_PREFILL: Record<BillingChargeKind, string> = {
  initial: '400000',
  monthly: '100000',
  other: '',
};

/** 支払方法の切替確認ダイアログに出す方式別の注意書き（TP-27 / TP-34）。 */
const METHOD_CONFIRM_NOTE: Record<PaymentMethod, string> = {
  card: '・振込・口座振替請求の新規発行はできなくなります（既存請求は残ります）\n\n',
  bank_transfer:
    '・振込請求（請求書発行 / 入金の手動消し込み）の対象になります\n' +
    '・カード決済（PAY.JP）の自動課金とは併用しないでください\n\n',
  direct_debit:
    '・口座振替請求（請求書発行 → 快速ねっとへ請求情報入力 / 引落結果の手動消し込み）の対象になります\n' +
    '・預金口座振替依頼書の福井銀行への提出と振替登録の完了を確認してから設定してください\n' +
    '・カード決済（PAY.JP）の自動課金とは併用しないでください\n\n',
};

/** 支払方法切替ボタン: 手動請求（振込 / 口座振替）へ = ブランド色の面、カードへ戻す = 面なし。 */
const METHOD_BUTTON_CLASS: Record<PaymentMethod, string> = {
  card: ROW_BTN_GHOST,
  bank_transfer: ROW_BTN_BRAND,
  direct_debit: ROW_BTN_BRAND,
};

const PAYMENT_METHODS: readonly PaymentMethod[] = [
  'card',
  'bank_transfer',
  'direct_debit',
];

/** 帳票（請求書 / 領収書）へのリンク。ゼブラ行の上なので面を持たせず文字色で示す。 */
const DOC_LINK = 'text-label font-medium text-brand-ink underline underline-offset-2';

/** 実在月のみ（サーバ zod の billingMonth regex と対称）。 */
const VALID_MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/** 実在する暦日か（サーバ zod の dueDate refine と対称 / '2026-02-31' を拒否）。 */
function isValidCalendarDate(v: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
}

const FIELD = 'flex flex-col gap-1.5';
const FIELD_LABEL = 'text-label font-medium text-ink-2';

/**
 * TP-27/TP-34: 振込・口座振替請求管理ページ（saas-admin 運営者ビュー）。
 *
 * - 支払方法の設定: tenants.paymentMethod を「カード / 銀行振込 / 口座振替」で切替（billingSetPaymentMethod）
 * - 請求の発行: 振込・口座振替テナントに billing_charges（pending）を発行
 *   （billingCreateBankTransferCharge / charge にはテナントの現在の支払方法を記録）。
 *   金額は税抜本体で入力し、消費税は外税でサーバ側加算。支払期限の既定は翌月末日（契約 第6条3項）
 * - 請求一覧: 全手動請求の横断一覧（請求済 / 滞納 / 入金済・引落済 / 取消）+ 手動消し込み・取消 + 帳票リンク
 *
 * 入金 / 引落成立の確認は完全手動（銀行 API 連携なし / issue スコープ外）。
 * 口座振替の引き落とし実行は快速ねっと側の操作（RUNBOOK §6.3h）で、本ページは記録のみ持つ。
 * 操作は全て audit_logs に記録される。
 * S-137: 見た目は docs/UI-GUIDE.md の v2（白カード 3 枚・ZebraTable・Chip・入力は @kawai/ui）。
 */
export function BankTransferBillingPage() {
  const { email, signOut } = useAuth();
  const today = todayJst();
  const { data: tenants, isLoading: tenantsLoading, error: tenantsError } = useTenants();
  const {
    data: charges,
    isLoading: chargesLoading,
    error: chargesError,
  } = useBankTransferCharges();
  const setPaymentMethod = useBillingSetPaymentMethod();
  const createCharge = useCreateBankTransferCharge();
  const updateCharge = useUpdateBankTransferCharge();

  const [opError, setOpError] = useState<string | null>(null);
  const [opNotice, setOpNotice] = useState<string | null>(null);

  const offlineTenants = useMemo(
    () => (tenants ?? []).filter((t) => isOfflinePaymentMethod(t.paymentMethod)),
    [tenants],
  );
  const tenantNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tenants ?? []) m.set(t.id, t.name);
    return m;
  }, [tenants]);

  // ---- 請求作成フォーム ----
  const currentMonth = today.slice(0, 7);
  const [formTenantId, setFormTenantId] = useState('');
  const [formKind, setFormKind] = useState<BillingChargeKind>('monthly');
  const [formBillingMonth, setFormBillingMonth] = useState(currentMonth);
  const [formNetYen, setFormNetYen] = useState(NET_YEN_PREFILL.monthly);
  const [formDueDate, setFormDueDate] = useState(() =>
    defaultBankTransferDueDate({
      kind: 'monthly',
      billingMonth: currentMonth,
      issuedDate: today,
    }),
  );
  // 入金期限を手動編集したら、種別 / 対象月の変更による自動再計算で上書きしない。
  const [dueDateTouched, setDueDateTouched] = useState(false);
  const [formDescription, setFormDescription] = useState('');

  const anyPending =
    setPaymentMethod.isPending || createCharge.isPending || updateCharge.isPending;

  function recomputeDueDate(kind: BillingChargeKind, billingMonth: string) {
    if (dueDateTouched) return;
    setFormDueDate(defaultBankTransferDueDate({ kind, billingMonth, issuedDate: today }));
  }

  function handleKindChange(kind: BillingChargeKind) {
    setFormKind(kind);
    setFormNetYen(NET_YEN_PREFILL[kind]);
    recomputeDueDate(kind, formBillingMonth);
  }

  function handleBillingMonthChange(month: string) {
    setFormBillingMonth(month);
    // 厳格 regex（サーバ zod と対称）。type="month" 非対応ブラウザ（テキスト degrade）で
    // '2026-13' 等が入ると endOfNextMonth が throw するため、実在月のみ再計算する。
    if (formKind === 'monthly' && VALID_MONTH_RE.test(month)) {
      recomputeDueDate('monthly', month);
    }
  }

  const netYenNumber = Number(formNetYen);
  const netYenValid =
    Number.isInteger(netYenNumber) && netYenNumber >= 1 && netYenNumber <= 9_999_999;
  const preview = netYenValid ? computeTaxFromNet(netYenNumber) : null;
  // 選択済みテナントが支払方法の切替（同ページ上部）で手動請求でなくなった場合も
  // 発行ボタンを無効化する（stale な formTenantId での発行はサーバ側でも拒否されるが、
  // 「支払方法: 不明」の confirm を出さない）。
  const formValid =
    offlineTenants.some((t) => t.id === formTenantId) &&
    netYenValid &&
    isValidCalendarDate(formDueDate) &&
    (formKind !== 'monthly' || VALID_MONTH_RE.test(formBillingMonth)) &&
    (formKind !== 'other' || formDescription.trim() !== '');

  async function handleCreate() {
    if (!formValid || anyPending || !preview) return;
    const formTenantMethod = offlineTenants.find(
      (t) => t.id === formTenantId,
    )?.paymentMethod;
    const confirmed = window.confirm(
      `テナント "${formTenantId}" に請求を発行します。\n\n` +
        `・支払方法: ${formTenantMethod ? paymentMethodLabel(formTenantMethod) : '不明'}\n` +
        `・種別: ${billingChargeKindLabel(formKind)}` +
        (formKind === 'monthly' ? `（対象月 ${formBillingMonth}）\n` : '\n') +
        `・金額: 税抜 ${formatYen(preview.netYen)} ＋ 消費税 ${formatYen(preview.taxYen)} ＝ 税込 ${formatYen(preview.grossYen)}\n` +
        `・支払期限: ${formDueDate}\n\n` +
        '発行後は一覧の「請求書」から帳票を印刷できます。続行しますか？',
    );
    if (!confirmed) return;
    setOpError(null);
    setOpNotice(null);
    try {
      const res = await createCharge.mutateAsync({
        tenantId: formTenantId,
        kind: formKind,
        netYen: preview.netYen,
        dueDate: formDueDate,
        ...(formKind === 'monthly' ? { billingMonth: formBillingMonth } : {}),
        ...(formDescription.trim() !== '' ? { description: formDescription.trim() } : {}),
      });
      setOpNotice(
        `${formTenantId} に請求（税込 ${formatYen(res.amountYen)}）を発行しました。`,
      );
      setFormDescription('');
    } catch (err) {
      setOpError(bankTransferBillingErrorMessage('請求の発行', err));
    }
  }

  async function handleSetPaymentMethod(tenant: Tenant, next: PaymentMethod) {
    if (anyPending) return;
    const confirmed = window.confirm(
      `テナント "${tenant.id}" の支払方法を「${paymentMethodLabel(next)}」に変更します。\n\n` +
        METHOD_CONFIRM_NOTE[next] +
        '続行しますか？',
    );
    if (!confirmed) return;
    setOpError(null);
    setOpNotice(null);
    try {
      await setPaymentMethod.mutateAsync({
        tenantId: tenant.id,
        paymentMethod: next,
      });
      setOpNotice(
        `${tenant.id} の支払方法を「${paymentMethodLabel(next)}」に変更しました。`,
      );
    } catch (err) {
      setOpError(bankTransferBillingErrorMessage('支払方法の変更', err));
    }
  }

  async function handleUpdateCharge(
    charge: BillingCharge,
    action: 'mark_paid' | 'unmark_paid' | 'cancel',
  ) {
    if (anyPending) return;
    // 口座振替（TP-34）は mark_paid＝引落成立の確認。文言だけ方式で切り替える（操作は同型）。
    const isDirectDebit = charge.paymentMethod === 'direct_debit';
    const paidWord = isDirectDebit ? '引落済' : '入金済';
    const messages: Record<typeof action, string> = {
      mark_paid:
        `請求（${charge.description ?? charge.id} / 税込 ${formatYen(charge.amountYen)}）の${isDirectDebit ? '引き落とし成立' : '入金'}を確認済みにします。\n\n` +
        `・ステータスが「${paidWord}」になり、領収書を発行できるようになります\n・更新者と日時が記録されます\n\n続行しますか？`,
      unmark_paid: `請求（${charge.description ?? charge.id}）の消し込みを取り消します。\n\n・ステータスが「請求済（${isDirectDebit ? '引落待ち' : '入金待ち'}）」に戻ります\n\n続行しますか？`,
      cancel:
        `請求（${charge.description ?? charge.id} / 税込 ${formatYen(charge.amountYen)}）を取り消します。\n\n` +
        '・ステータスが「取消」になります（記録は残ります）\n・同じ対象月に再発行できるようになります\n\n続行しますか？',
    };
    if (!window.confirm(messages[action])) return;
    setOpError(null);
    setOpNotice(null);
    const doneLabel: Record<typeof action, string> = {
      mark_paid: `${paidWord}にしました`,
      unmark_paid: '消し込みを取り消しました',
      cancel: '請求を取り消しました',
    };
    try {
      await updateCharge.mutateAsync({
        tenantId: charge.tenantId,
        chargeId: charge.id,
        action,
      });
      setOpNotice(`${charge.tenantId} の請求を${doneLabel[action]}。`);
    } catch (err) {
      setOpError(bankTransferBillingErrorMessage('請求の更新', err));
    }
  }

  return (
    <main className="mx-auto max-w-[1100px] p-6">
      <PageHeader
        title="振込・口座振替請求管理"
        description="銀行振込・口座振替テナントの請求書発行・消し込み（saas-admin）"
        email={email}
        links={[
          { to: '/billing', label: '課金管理' },
          { to: '/audit-logs', label: '監査ログ' },
        ]}
        onSignOut={() => {
          void signOut();
        }}
      />

      {/* ---- 支払方法の設定 ---- */}
      <section className={`mt-6 ${CARD}`} aria-label="支払方法の設定">
        <h2 className={CARD_TITLE}>支払方法の設定</h2>
        <p className="mt-2 text-label text-ink-3">
          銀行振込は入金を銀行口座の目視で、口座振替は快速ねっとの引落結果で確認し、
          「入金確認 / 引落確認」で手動消し込みします（自動照合なし / 運用手順は RUNBOOK
          §6.3g・§6.3h）。 月額の支払期限は対象月の翌月末日（契約
          第6条3項）、消費税は外税で自動加算されます。
        </p>

        {tenantsError && (
          <Notice role="alert" className="mt-4">
            テナント一覧の取得に失敗しました: {tenantsError.message}
          </Notice>
        )}
        {opError && (
          <Notice role="alert" className="mt-4">
            {opError}
          </Notice>
        )}
        {opNotice && (
          <Notice role="status" aria-live="polite" tone="success" className="mt-4">
            {opNotice}
          </Notice>
        )}

        {tenantsLoading ? (
          <p role="status" aria-live="polite" className="mt-4 text-body text-ink-3">
            読み込み中…
          </p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <ZebraTable aria-label="テナント別の支払方法">
              <thead>
                <ZebraTable.HeadRow>
                  <ZebraTable.Th edge="first">tenantId</ZebraTable.Th>
                  <ZebraTable.Th>name</ZebraTable.Th>
                  <ZebraTable.Th>支払方法</ZebraTable.Th>
                  <ZebraTable.Th edge="last">操作</ZebraTable.Th>
                </ZebraTable.HeadRow>
              </thead>
              <tbody>
                {(tenants ?? []).map((t) => (
                  <ZebraTable.Row key={t.id} className="align-top">
                    <ZebraTable.Td className={MONO}>{t.id}</ZebraTable.Td>
                    <ZebraTable.Td>{t.name}</ZebraTable.Td>
                    <ZebraTable.Td>
                      {t.paymentMethod ? paymentMethodLabel(t.paymentMethod) : '未設定'}
                    </ZebraTable.Td>
                    <ZebraTable.Td>
                      <div className="flex flex-wrap gap-1">
                        {PAYMENT_METHODS.filter((m) => m !== t.paymentMethod).map(
                          (next) => (
                            <button
                              key={next}
                              type="button"
                              onClick={() => {
                                void handleSetPaymentMethod(t, next);
                              }}
                              disabled={anyPending}
                              className={METHOD_BUTTON_CLASS[next]}
                            >
                              {paymentMethodLabel(next)}にする
                            </button>
                          ),
                        )}
                      </div>
                    </ZebraTable.Td>
                  </ZebraTable.Row>
                ))}
              </tbody>
            </ZebraTable>
          </div>
        )}
      </section>

      {/* ---- 請求の発行 ---- */}
      <section className={`mt-6 ${CARD}`} aria-label="振込・口座振替請求の発行フォーム">
        <h2 className={CARD_TITLE}>請求の発行</h2>
        {offlineTenants.length === 0 ? (
          <p className="mt-4 text-body text-ink-3">
            銀行振込・口座振替のテナントがありません。上の表で支払方法を設定してください。
          </p>
        ) : (
          <>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <label className={FIELD}>
                <span className={FIELD_LABEL}>テナント</span>
                <Select
                  value={formTenantId}
                  onChange={(e) => setFormTenantId(e.target.value)}
                >
                  <option value="">選択してください</option>
                  {offlineTenants.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.id}（{t.name} /{' '}
                      {t.paymentMethod ? paymentMethodLabel(t.paymentMethod) : '未設定'}）
                    </option>
                  ))}
                </Select>
              </label>
              <label className={FIELD}>
                <span className={FIELD_LABEL}>種別</span>
                <Select
                  value={formKind}
                  onChange={(e) => handleKindChange(e.target.value as BillingChargeKind)}
                >
                  <option value="monthly">月額</option>
                  <option value="initial">初期費用</option>
                  <option value="other">その他</option>
                </Select>
              </label>
              {formKind === 'monthly' && (
                <label className={FIELD}>
                  <span className={FIELD_LABEL}>対象月</span>
                  <Input
                    type="month"
                    value={formBillingMonth}
                    onChange={(e) => handleBillingMonthChange(e.target.value)}
                  />
                </label>
              )}
              <label className={FIELD}>
                <span className={FIELD_LABEL}>金額（税抜・円）</span>
                <Input
                  type="number"
                  min={1}
                  max={9999999}
                  step={1}
                  value={formNetYen}
                  onChange={(e) => setFormNetYen(e.target.value)}
                />
              </label>
              <label className={FIELD}>
                <span className={FIELD_LABEL}>支払期限</span>
                <Input
                  type="date"
                  value={formDueDate}
                  onChange={(e) => {
                    setDueDateTouched(true);
                    setFormDueDate(e.target.value);
                  }}
                />
              </label>
              <label className={`${FIELD} col-span-full`}>
                <span className={FIELD_LABEL}>
                  取引内容（省略時は自動生成
                  {formKind === 'other' ? ' / その他は必須' : ''}）
                </span>
                <Input
                  type="text"
                  maxLength={200}
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder={
                    formKind === 'monthly'
                      ? `月額利用料 ${formBillingMonth}`
                      : formKind === 'initial'
                        ? '初期費用'
                        : '例: 初期設定作業費'
                  }
                />
              </label>
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
              <span className="text-body text-ink-2">
                {preview
                  ? `税抜 ${formatYen(preview.netYen)} ＋ 消費税(10%) ${formatYen(preview.taxYen)} ＝ 税込 ${formatYen(preview.grossYen)}`
                  : '金額（税抜）を入力してください'}
              </span>
              <Button
                type="button"
                onClick={() => {
                  void handleCreate();
                }}
                disabled={!formValid || anyPending}
              >
                {createCharge.isPending ? '発行中…' : '請求を発行'}
              </Button>
            </div>
          </>
        )}
      </section>

      {/* ---- 請求一覧 ---- */}
      <section className={`mt-6 ${CARD}`} aria-label="請求一覧">
        <h2 className={CARD_TITLE}>請求一覧（銀行振込・口座振替）</h2>
        {chargesLoading && (
          <p role="status" aria-live="polite" className="mt-4 text-body text-ink-3">
            請求一覧を読み込み中…
          </p>
        )}
        {chargesError && (
          <Notice role="alert" className="mt-4">
            請求一覧の取得に失敗しました: {chargesError.message}
          </Notice>
        )}
        {!chargesLoading && !chargesError && (
          <div className="mt-4 overflow-x-auto">
            <ZebraTable aria-label="銀行振込・口座振替の請求">
              <thead>
                <ZebraTable.HeadRow>
                  <ZebraTable.Th edge="first">発行日</ZebraTable.Th>
                  <ZebraTable.Th>テナント</ZebraTable.Th>
                  <ZebraTable.Th>支払方法</ZebraTable.Th>
                  <ZebraTable.Th>種別</ZebraTable.Th>
                  <ZebraTable.Th>対象月</ZebraTable.Th>
                  <ZebraTable.Th align="right">金額（税込）</ZebraTable.Th>
                  <ZebraTable.Th>支払期限</ZebraTable.Th>
                  <ZebraTable.Th>状態</ZebraTable.Th>
                  <ZebraTable.Th>帳票</ZebraTable.Th>
                  <ZebraTable.Th edge="last">操作</ZebraTable.Th>
                </ZebraTable.HeadRow>
              </thead>
              <tbody>
                {(charges ?? []).length === 0 ? (
                  <ZebraTable.Row>
                    <ZebraTable.Td colSpan={10} align="center" tone="muted" padding="lg">
                      銀行振込・口座振替の請求がありません
                    </ZebraTable.Td>
                  </ZebraTable.Row>
                ) : (
                  (charges ?? []).map((c) => {
                    const display = resolveBankTransferDisplayStatus(
                      c.status,
                      c.dueDate,
                      today,
                    );
                    return (
                      <ZebraTable.Row key={c.id} className="align-top">
                        <ZebraTable.Td className="whitespace-nowrap">
                          {c.createdAt.slice(0, 10)}
                        </ZebraTable.Td>
                        <ZebraTable.Td className={MONO}>
                          <Link
                            to={`/billing/${c.tenantId}`}
                            className="text-ink underline underline-offset-2"
                          >
                            {c.tenantId}
                          </Link>
                          <div className="font-sans text-note text-ink-3">
                            {tenantNameById.get(c.tenantId) ?? ''}
                          </div>
                        </ZebraTable.Td>
                        <ZebraTable.Td className="whitespace-nowrap">
                          {paymentMethodLabel(c.paymentMethod)}
                        </ZebraTable.Td>
                        <ZebraTable.Td>{billingChargeKindLabel(c.kind)}</ZebraTable.Td>
                        <ZebraTable.Td>{c.billingMonth ?? '—'}</ZebraTable.Td>
                        <ZebraTable.Td numeric>{formatYen(c.amountYen)}</ZebraTable.Td>
                        <ZebraTable.Td className="whitespace-nowrap">
                          {c.dueDate ?? '—'}
                        </ZebraTable.Td>
                        <ZebraTable.Td>
                          <Chip tone={bankTransferStatusChipTone(display)}>
                            {bankTransferDisplayStatusLabel(display, c.paymentMethod)}
                          </Chip>
                          {c.status === 'succeeded' && c.paidAt && (
                            <div className="mt-1 text-note text-ink-3">
                              {c.paymentMethod === 'direct_debit' ? '引落' : '入金'}{' '}
                              {c.paidAt.slice(0, 10)}
                            </div>
                          )}
                        </ZebraTable.Td>
                        <ZebraTable.Td>
                          <div className="flex gap-3 whitespace-nowrap">
                            {c.status !== 'failed' && (
                              <Link
                                to={`/billing/${c.tenantId}/invoice/${c.id}?type=invoice`}
                                target="_blank"
                                className={DOC_LINK}
                              >
                                請求書
                              </Link>
                            )}
                            {c.status === 'succeeded' && (
                              <Link
                                to={`/billing/${c.tenantId}/invoice/${c.id}?type=receipt`}
                                target="_blank"
                                className={DOC_LINK}
                              >
                                領収書
                              </Link>
                            )}
                          </div>
                        </ZebraTable.Td>
                        <ZebraTable.Td>
                          <div className="flex flex-wrap gap-1">
                            {c.status === 'pending' && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => {
                                    void handleUpdateCharge(c, 'mark_paid');
                                  }}
                                  disabled={anyPending}
                                  className={ROW_BTN_BRAND}
                                >
                                  {c.paymentMethod === 'direct_debit'
                                    ? '引落確認'
                                    : '入金確認'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    void handleUpdateCharge(c, 'cancel');
                                  }}
                                  disabled={anyPending}
                                  className={ROW_BTN_DANGER}
                                >
                                  取消
                                </button>
                              </>
                            )}
                            {c.status === 'succeeded' && (
                              <button
                                type="button"
                                onClick={() => {
                                  void handleUpdateCharge(c, 'unmark_paid');
                                }}
                                disabled={anyPending}
                                className={ROW_BTN_GHOST}
                              >
                                消し込み取消
                              </button>
                            )}
                            {(c.status === 'failed' || c.status === 'refunded') && (
                              <span className="text-ink-3">—</span>
                            )}
                          </div>
                        </ZebraTable.Td>
                      </ZebraTable.Row>
                    );
                  })
                )}
              </tbody>
            </ZebraTable>
          </div>
        )}
      </section>
    </main>
  );
}
